import { useMemo, useState } from "preact/hooks";
import { useResource } from "@tenergy/web-core/dashboard";
import { shortCode } from "@tenergy/web-core/format";
import { adminReads } from "../api/admin-api.js";
import type { AdminOrder, AdminOrderKind } from "../api/types.js";
import { centiSunToSun, count, trx, utc } from "../format.js";
import type { Router } from "../router.js";
import {
  Address,
  Blank,
  Button,
  ErrorNote,
  Fact,
  Note,
  Panel,
  ORDER_STATUS_LABEL,
  Pill,
  ScreenHeader,
  ShortId,
  Spinner,
  StatRow,
  Table,
  Td,
  Th,
  Tile,
  TxId,
  inputClass,
} from "../ui.js";

/**
 * Order search and one order's detail.
 *
 * WHAT THE ENDPOINT FILTERS AND WHAT THIS SCREEN FILTERS ARE DIFFERENT, and the screen says
 * so. `GET /v1/admin/orders` takes `status`, `receiver` and `account_id` and nothing else — no
 * id, no short code, no `client_order_id`. Those three are matched HERE, over the page the
 * endpoint returned, which finds a recent order and will not find an old one. Pretending
 * otherwise would be worse than saying it. A full order id always opens directly
 * (`GET /v1/admin/orders/{id}`), however old the order is.
 */

const PAGE_LIMIT = 100;

/** The order types (owner 2026-10-24: «где тип заказа? энергия band, рефилл, активация и тп?»). */
export const KIND_LABEL: Record<AdminOrderKind, string> = {
  energy: "Energy",
  bandwidth: "Bandwidth",
  activation: "Activation",
  refill: "Refill",
  renewal: "Renewal",
  promo: "Promo grant",
  activator_rental: "Activator rental",
};

/** The owner's delivery scale in blocks of 3 s: 1 excellent, 2 good, 3 acceptable, 4 bad, 5+ fallback only. */
export function deliveryTone(blocks: number): "good" | "neutral" | "attention" | "bad" {
  if (blocks <= 2) return "good";
  if (blocks === 3) return "neutral";
  if (blocks === 4) return "attention";
  return "bad";
}

const DELIVERY_FROM: Record<string, string> = {
  block: "from the payment block",
  paid: "from the payment",
  created: "from the order creation (no payment event)",
};

/** Nearest-rank percentile of a sorted list. */
export function percentile(sorted: readonly number[], p: number): number | null {
  if (sorted.length === 0) return null;
  return sorted[Math.min(sorted.length - 1, Math.max(0, Math.ceil((p / 100) * sorted.length) - 1))]!;
}
const DELIVERED = new Set(["confirmed", "active", "expired", "reclaimed"]);
const IN_FLIGHT = new Set(["created", "paid", "allocating", "delegated"]);

/** A term matches an order by full id, by its short code, or by `client_order_id`. */
export function matchesTerm(order: AdminOrder, term: string): boolean {
  const needle = term.trim().toLowerCase();
  if (needle === "") return true;
  if (order.id.toLowerCase().includes(needle)) return true;
  if (shortCode(order.id).toLowerCase().includes(needle)) return true;
  if (order.client_order_id !== null && order.client_order_id.toLowerCase().includes(needle)) {
    return true;
  }
  return false;
}

export function OrdersScreen({ router }: { router: Router }) {
  const [term, setTerm] = useState("");
  const [status, setStatus] = useState("");
  const [receiver, setReceiver] = useState("");
  const [accountId, setAccountId] = useState("");
  const [kind, setKind] = useState("");
  const [slowestFirst, setSlowestFirst] = useState(false);

  const orders = useResource(
    (signal) => {
      void signal;
      return adminReads.orders({
        ...(status === "" ? {} : { status }),
        ...(receiver === "" ? {} : { receiver }),
        ...(accountId === "" ? {} : { accountId }),
        ...(kind === "" ? {} : { kind }),
        limit: PAGE_LIMIT,
      });
    },
    [status, receiver, accountId, kind],
  );

  const pageRows = orders.data?.data ?? [];
  const rows = useMemo(() => {
    const matched = (orders.data?.data ?? []).filter((order) => matchesTerm(order, term));
    if (!slowestFirst) return matched;
    // The slowest first; an order with no delivery (failed, refunded, in flight) last.
    return [...matched].sort((a, b) => (b.delivery?.seconds ?? -1) - (a.delivery?.seconds ?? -1));
  }, [orders.data, term, slowestFirst]);
  const deliveries = useMemo(
    () => pageRows.flatMap((row) => (row.delivery == null ? [] : [row.delivery])).sort((a, b) => a.seconds - b.seconds),
    [pageRows],
  );
  const p50 = percentile(deliveries.map((d) => d.seconds), 50);
  const p95 = percentile(deliveries.map((d) => d.seconds), 95);
  const overThree = deliveries.filter((d) => d.blocks > 3).length;

  return (
    <>
      <ScreenHeader
        title="Orders"
        lead="Status, receiver and account are filtered by the API. Short code, full id and client order id are matched over the page it returned — the endpoint has no filter for them."
      />

      <StatRow>
        <Tile
          icon="list"
          label="Orders on this page"
          value={orders.data == null ? null : `${pageRows.length}${pageRows.length === PAGE_LIMIT ? "+" : ""}`}
          note={`Newest first, at most ${PAGE_LIMIT}, with the filters below.`}
        />
        <Tile
          icon="bolt"
          label="Delivered"
          tone="good"
          value={orders.data == null ? null : String(pageRows.filter((row) => DELIVERED.has(row.status)).length)}
          note="confirmed, active, expired or reclaimed — on this page."
        />
        <Tile
          icon="clock"
          label="In flight"
          value={orders.data == null ? null : String(pageRows.filter((row) => IN_FLIGHT.has(row.status)).length)}
          note="created, paid, allocating or delegated — on this page."
        />
        <Tile
          icon="bell"
          label="Failed or refunded"
          tone={pageRows.some((row) => row.status === "failed") ? "danger" : "neutral"}
          value={orders.data == null ? null : String(pageRows.filter((row) => row.status === "failed" || row.status === "refunded").length)}
          note="On this page. Alerts lists the failures of the last 24 h."
        />
      </StatRow>

      <StatRow>
        <Tile
          icon="clock"
          label="Delivery p50"
          value={orders.data == null ? null : p50 === null ? "—" : `${p50}`}
          unit={p50 === null ? undefined : "s"}
          tone={p50 !== null && p50 > 9 ? "warning" : "neutral"}
          note={`Payment → delegation on chain, ${deliveries.length} delivered orders on this page.`}
        />
        <Tile
          icon="clock"
          label="Delivery p95"
          value={orders.data == null ? null : p95 === null ? "—" : `${p95}`}
          unit={p95 === null ? undefined : "s"}
          tone={p95 !== null && p95 > 12 ? "danger" : p95 !== null && p95 > 9 ? "warning" : "neutral"}
          note="3 s a block: up to 9 s is 3 blocks, past 12 s is 5 or more."
        />
        <Tile
          icon="bell"
          label="Slower than 3 blocks"
          value={orders.data == null ? null : String(overThree)}
          tone={overThree > 0 ? "danger" : "good"}
          note="Delivered in 4 blocks or more — bad (4) or fallback only (5+), on this page."
        />
      </StatRow>

      <Panel>
        <div class="grid gap-2 sm:grid-cols-5">
          <input
            class={inputClass}
            aria-label="Short code, full id or client order id"
            placeholder="short code / id / client_order_id"
            value={term}
            onInput={(event) => setTerm((event.currentTarget as HTMLInputElement).value)}
          />
          <input
            class={inputClass}
            aria-label="Receiver"
            placeholder="receiver address"
            value={receiver}
            onInput={(event) => setReceiver((event.currentTarget as HTMLInputElement).value.trim())}
          />
          <input
            class={inputClass}
            aria-label="Account id"
            placeholder="account id"
            value={accountId}
            onInput={(event) => setAccountId((event.currentTarget as HTMLInputElement).value.trim())}
          />
          <select
            class={inputClass}
            aria-label="Status"
            value={status}
            onChange={(event) => setStatus((event.currentTarget as HTMLSelectElement).value)}
          >
            <option value="">every status</option>
            {["created", "paid", "allocating", "delegated", "confirmed", "active", "expired", "reclaimed", "failed", "refunded"].map(
              (value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ),
            )}
          </select>
          <select
            class={inputClass}
            aria-label="Type"
            value={kind}
            onChange={(event) => setKind((event.currentTarget as HTMLSelectElement).value)}
          >
            <option value="">every type</option>
            {(Object.keys(KIND_LABEL) as AdminOrderKind[]).map((value) => (
              <option key={value} value={value}>
                {KIND_LABEL[value]}
              </option>
            ))}
          </select>
        </div>
      </Panel>

      <div class="mt-3">
        <Panel
          title={`Orders (${rows.length}${rows.length === PAGE_LIMIT ? "+" : ""})`}
          subtitle={`Newest first, at most ${PAGE_LIMIT} from the API before the term above is applied.`}
        >
          {orders.loading ? (
            <Spinner />
          ) : orders.error !== null ? (
            <ErrorNote error={orders.error} onRetry={() => orders.reload()} />
          ) : (
            <Table
              rows={rows.length}
              empty="No order matches. A term matches only inside the page the API returned; narrow by receiver or account to reach older orders."
              head={
                <tr>
                  <Th>Order</Th>
                  <Th>Type</Th>
                  <Th>Created</Th>
                  <Th>Account</Th>
                  <Th>Receiver</Th>
                  <Th>Tier</Th>
                  <Th numeric>Amount</Th>
                  <Th numeric>Delivered</Th>
                  <Th numeric>Price</Th>
                  <Th numeric>Paid</Th>
                  <Th numeric>
                    <button
                      type="button"
                      class="font-medium hover:underline"
                      aria-pressed={slowestFirst}
                      title="Payment → delegation on chain; click to sort the slowest first"
                      onClick={() => setSlowestFirst((value) => !value)}
                    >
                      Delivery{slowestFirst ? " ↓" : ""}
                    </button>
                  </Th>
                  <Th>Status</Th>
                  <Th>{""}</Th>
                </tr>
              }
            >
              {rows.map((order) => (
                <tr key={order.id}>
                  <Td mono>
                    <ShortId value={order.id} />
                  </Td>
                  <Td>
                    {order.kind === undefined ? <Blank /> : KIND_LABEL[order.kind] ?? order.kind}
                    <div class="text-[11px] text-ink-muted">{order.source}</div>
                  </Td>
                  <Td mono>{utc(order.created_at)}</Td>
                  <Td mono>
                    <ShortId value={order.account_id} />
                  </Td>
                  <Td mono>
                    <Address value={order.receiver} />
                  </Td>
                  <Td>{order.tier ?? <Blank />}</Td>
                  <Td numeric>{count(order.amount)}</Td>
                  <Td numeric>{count(order.delivered_amount)}</Td>
                  <Td numeric>{order.price_sun_per_unit ?? <Blank />}</Td>
                  <Td numeric>{trx(order.total_amount_sun)}</Td>
                  <Td numeric>
                    {order.delivery == null ? (
                      "—"
                    ) : (
                      <span title={DELIVERY_FROM[order.delivery.from] ?? ""}>
                        <Pill value={`${order.delivery.seconds} s · ${order.delivery.blocks} bl`} tone={deliveryTone(order.delivery.blocks)} />
                      </span>
                    )}
                  </Td>
                  <Td>
                    <Pill value={ORDER_STATUS_LABEL[order.status] ?? order.status} />
                  </Td>
                  <Td>
                    <a
                      href={router.hrefFor?.("orders", order.id) ?? `/orders/${encodeURIComponent(order.id)}`}
                      onClick={(event) => router.onNavigate(event as MouseEvent, "orders", order.id)}
                      class="text-[11.5px] font-medium text-accent-text hover:underline"
                    >
                      open
                    </a>
                  </Td>
                </tr>
              ))}
            </Table>
          )}
        </Panel>
      </div>
    </>
  );
}

/**
 * One order, end to end: `GET /v1/admin/orders/{id}` (viewer).
 *
 * The order row, the ladder walk it was priced at (with the source ids a buyer never sees), the
 * allocations that filled it, every chain transaction written for it, and its event history.
 * Any order can be opened by id, however old; the signed transaction bytes are never sent.
 */
export function OrderDetailScreen({ id, router }: { id: string; router: Router }) {
  const detail = useResource(
    (signal) => {
      void signal;
      return adminReads.order(id);
    },
    [id],
  );
  const view = detail.data;
  const order = view?.order ?? null;

  return (
    <>
      <ScreenHeader
        title={`Order ${shortCode(id)}`}
        lead={id}
        action={
          <Button tone="ghost" onClick={() => router.navigate("orders")}>
            Back to orders
          </Button>
        }
      />

      {detail.loading && view == null ? (
        <Spinner />
      ) : detail.error !== null ? (
        <ErrorNote error={detail.error} onRetry={() => detail.reload()} />
      ) : view == null || order === null ? null : (
        <div class="space-y-3">
          <StatRow>
            <Tile icon="list" label="Status" value={order.status} note={order.confirm_status === null ? null : `chain: ${order.confirm_status}`} tone={order.status === "failed" ? "danger" : order.status === "confirmed" || order.status === "active" ? "good" : "neutral"} />
            <Tile
              icon="bolt"
              label="Delivered"
              value={order.delivered_amount === null ? null : count(order.delivered_amount)}
              unit={order.resource}
              note={`of ${count(order.amount)} ordered${order.partial ? " — partial" : ""}`}
            />
            <Tile icon="tag" label="Charged" value={trx(order.total_amount_sun)} note={`refunded ${trx(order.refunded_amount_sun)}`} />
            <Tile
              icon="stack"
              label="Supply cost"
              value={view.cost_sun === null ? null : trx(view.cost_sun)}
              note={view.cost_sun === null ? "No source has charged for this order yet." : "What the platform paid its sources: the allocations below."}
            />
          </StatRow>

          <Panel title="Order">
            <dl class="grid gap-x-6 sm:grid-cols-2 lg:grid-cols-3">
              <Fact label="Account" value={<ShortId value={order.account_id} />} />
              <Fact label="Receiver" value={<Address value={order.receiver} />} />
              <Fact label="Client order id" value={order.client_order_id ?? "—"} mono />
              <Fact label="Resource / tier" value={`${order.resource} / ${order.tier ?? "—"}`} />
              <Fact label="Price per unit" value={order.price_sun_per_unit === null ? "—" : `${order.price_sun_per_unit} SUN`} mono />
              <Fact label="Source" value={order.source} />
              <Fact label="Created" value={utc(order.created_at)} mono />
              <Fact label="Delegated" value={utc(order.delegated_at)} mono />
              <Fact label="Expires" value={utc(order.expires_at)} mono />
              <Fact label="Reclaimed" value={utc(order.reclaimed_at)} mono />
              <Fact label="Updated" value={utc(order.updated_at)} mono />
              <Fact label="Memo" value={order.memo ?? "—"} />
            </dl>
          </Panel>

          {order.failure === null ? null : (
            <Note tone="danger">
              {order.failure.code} {order.failure.slug}: {order.failure.message}
              {order.failure.at === undefined ? "" : ` (${utc(order.failure.at)})`}
            </Note>
          )}

          <div class="grid gap-3 xl:grid-cols-2">
            <Panel
              title="Fills"
              subtitle={
                view.unit_price_centi_sun === null
                  ? "The ladder walk this order was priced at."
                  : view.fills_priced_by === "venue_offers"
                    ? `Sold at ${centiSunToSun(view.unit_price_centi_sun)} per unit: priced from the venues' offers when the payment landed.`
                    : `The ladder walk this order was priced at; volume-weighted ${centiSunToSun(view.unit_price_centi_sun)} per unit.`
              }
            >
              <Table
                rows={view.fills.length}
                empty="This order was not priced from the ladder, so the cascade's own ranking governed it."
                head={
                  <tr>
                    <Th>Class</Th>
                    <Th>Sources</Th>
                    <Th numeric>Amount</Th>
                    <Th numeric>Price</Th>
                  </tr>
                }
              >
                {view.fills.map((fill, index) => (
                  <tr key={`${fill.class}-${index}`}>
                    <Td>
                      <Pill value={fill.class} />
                    </Td>
                    <Td mono>{fill.source_ids.length === 0 ? <Blank title="The stored walk carries no source id" /> : fill.source_ids.join(", ")}</Td>
                    <Td numeric>{count(fill.amount)}</Td>
                    <Td numeric>{fill.price_centi_sun === null ? <Blank /> : centiSunToSun(fill.price_centi_sun)}</Td>
                  </tr>
                ))}
              </Table>
            </Panel>

            <Panel title="Events" subtitle="The order's state history, append-only.">
              <Table
                rows={view.events.length}
                empty="No event is recorded for this order."
                head={
                  <tr>
                    <Th>#</Th>
                    <Th>At</Th>
                    <Th>Event</Th>
                    <Th>Transition</Th>
                    <Th>Actor</Th>
                  </tr>
                }
              >
                {view.events.map((event) => (
                  <tr key={event.seq}>
                    <Td numeric>{event.seq}</Td>
                    <Td mono>{utc(event.created_at)}</Td>
                    <Td mono>{event.event}</Td>
                    <Td>
                      {event.from_status === null && event.to_status === null ? (
                        <Blank />
                      ) : (
                        <span class="num text-[11.5px]">
                          {event.from_status ?? "∅"} → {event.to_status ?? "∅"}
                        </span>
                      )}
                    </Td>
                    <Td mono>{event.actor}</Td>
                  </tr>
                ))}
              </Table>
            </Panel>
          </div>

          <Panel title="Allocations" subtitle="Which source filled which part, at what cost. One row per source attempt.">
            <Table
              rows={view.allocations.length}
              empty="Nothing has been allocated yet. An order is allocated after it is paid."
              head={
                <tr>
                  <Th>#</Th>
                  <Th>Source</Th>
                  <Th>State</Th>
                  <Th numeric>Requested</Th>
                  <Th numeric>Delivered</Th>
                  <Th numeric>Cost</Th>
                  <Th numeric>Final cost</Th>
                  <Th>Delegate tx</Th>
                  <Th>Last error</Th>
                </tr>
              }
            >
              {view.allocations.map((row) => (
                <tr key={row.id}>
                  <Td numeric>{row.seq}</Td>
                  <Td>
                    <span class="num text-[11.5px]">{row.source_id}</span>
                    <span class="ml-1.5 text-[10.5px] text-ink-muted">{row.source_kind}</span>
                    {row.supply_address === null ? null : (
                      <div>
                        <Address value={row.supply_address} />
                      </div>
                    )}
                  </Td>
                  <Td>
                    <Pill value={row.state} />
                  </Td>
                  <Td numeric>{count(row.amount_requested)}</Td>
                  <Td numeric>{row.amount_delivered === null ? <Blank /> : count(row.amount_delivered)}</Td>
                  <Td numeric>{row.cost_sun === null ? <Blank /> : trx(row.cost_sun)}</Td>
                  <Td numeric>{row.final_cost_sun === null ? <Blank title="Known only for a source that settles at return" /> : trx(row.final_cost_sun)}</Td>
                  <Td mono>{row.delegate_txid === null ? <Blank /> : <TxId value={row.delegate_txid} />}</Td>
                  <Td class="max-w-[240px] text-[11.5px] text-ink-muted">
                    {row.last_error === null ? <Blank /> : `${row.last_error_code ?? ""} ${row.last_error}`}
                  </Td>
                </tr>
              ))}
            </Table>
          </Panel>

          <Panel title="Chain transactions" subtitle="Every chain write for this order, from intent to confirmation. The signed bytes stay in the database.">
            <Table
              rows={view.chain_txs.length}
              empty="Nothing has been broadcast for this order."
              head={
                <tr>
                  <Th>Tx</Th>
                  <Th>Purpose</Th>
                  <Th>Status</Th>
                  <Th>From → to</Th>
                  <Th numeric>SUN</Th>
                  <Th numeric>Block</Th>
                  <Th numeric>Fee</Th>
                  <Th>Broadcast</Th>
                </tr>
              }
            >
              {view.chain_txs.map((tx) => (
                <tr key={tx.txid}>
                  <Td mono>
                    <TxId value={tx.txid} />
                  </Td>
                  <Td mono>
                    {tx.purpose}
                    <span class="ml-1 text-ink-muted">({tx.kind})</span>
                  </Td>
                  <Td>
                    <Pill value={tx.status} />
                    {tx.rebroadcast_count > 0 ? (
                      <span class="num ml-1 text-[10.5px] text-warning">×{tx.rebroadcast_count}</span>
                    ) : null}
                  </Td>
                  <Td mono>
                    <Address value={tx.owner_address} />
                    {tx.receiver === null ? null : (
                      <>
                        {" → "}
                        <Address value={tx.receiver} />
                      </>
                    )}
                  </Td>
                  <Td numeric>{tx.sun === null ? <Blank /> : count(tx.sun)}</Td>
                  <Td numeric>{tx.block_number === null ? <Blank /> : count(tx.block_number)}</Td>
                  <Td numeric>{tx.fee_sun === null ? <Blank /> : trx(tx.fee_sun)}</Td>
                  <Td mono>{utc(tx.broadcast_at)}</Td>
                </tr>
              ))}
            </Table>
          </Panel>
        </div>
      )}
    </>
  );
}
